import { createStyles } from "@mantine/core";

// Dedicated styles for ClientNoteview (the "Notes" tab inside a client's
// profile). Kept separate from ./style.js (used by the standalone Notes
// module page) so a redesign of one can never silently break the other —
// they used to share one file, and when that file was rewritten for the
// Notes-module mockup, this tab lost `content`/`btn`/`rightBTN`/`table`/
// `searchInput` (removed/renamed there) and rendered unstyled.
const useStyles = createStyles((theme) => ({
  content: {
    display: "flex",
    width: "100%",
    flexDirection: "column",
    overflowX: "auto",
    backgroundColor: "var(--app-bg)",
  },
  btn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginTop: 50,
    backgroundColor: "var(--app-surface)",
    padding: "1rem",
    borderRadius: "10px",
    "@media (max-width: 480px)": {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "10px",
      padding: "12px",
    },
  },
  rightBTN: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    "& .mantine-Menu-dropdown": {
      padding: "0 !important",
    },
    "@media (max-width: 480px)": {
      flexWrap: "wrap",
      gap: "8px",
    },
  },
  table: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    borderRadius: "16px",
    background: "var(--app-surface)",
    padding: "12px 8px",
    gap: 12,
    marginBlock: 30,
  },
  searchInput: {
    width: "55%",
    height: "auto",
    backgroundColor: "var(--app-surface)",
    borderRadius: "8px",
    border: "1px solid var(--app-border)",
    boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
    "& .mantine-Input-input": {
      fontFamily: "Inter",
      border: "none",
      backgroundColor: "transparent",
      height: "44px",
    },
    "@media (max-width: 480px)": {
      width: "100%",
    },
  },
  FilterIcon: {
    padding: "5px",
    borderRadius: 3,
    cursor: "pointer",
    color: "var(--app-text-muted)",
  },
  box_header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "13px 15px",
    borderBottom: "1px solid var(--app-border)",
    "& .mantine-Text-root": {
      color: "var(--app-text)",
      fontWeight: 700,
      fontSize: "13.5px",
    },
  },
  box_body: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    background: "var(--app-bg)",
    padding: "12px 15px",
  },
  filterFields: {
    padding: "12px",
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: "8px",
  },
  btn_container: {
    display: "flex",
    justifyContent: "space-between",
    padding: "12px 15px",
    borderTop: "1px solid var(--app-border)",
  },
  filterCategoryList: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
  },
  filterCategoryItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 15px",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500,
    color: "var(--app-text)",
    borderBottom: "1px solid var(--app-border)",
    "&:hover": {
      backgroundColor: "var(--app-surface-2)",
    },
  },
  activeFilterDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "var(--app-primary)",
  },
  filterCategoryHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "16px 15px",
    borderBottom: "1px solid var(--app-border)",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
    color: "var(--app-text)",
  },
  activeFiltersBar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 14,
  },
  filterChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: 16,
    backgroundColor: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    fontSize: 12,
    fontWeight: 500,
    color: "var(--app-text)",
    "& svg": {
      cursor: "pointer",
      color: "var(--app-text-faint, #8A93A2)",
      "&:hover": {
        color: "var(--app-text)",
      },
    },
  },
  clearAllLink: {
    fontSize: 12,
    fontWeight: 600,
    color: "var(--app-text-muted)",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },

  // ---- Add Note dropdown — mirrors the standalone Notes module's
  // "Create Note" menu (see ./style.js) so both look identical. ----
  btnPrimary: {
    backgroundColor: "var(--app-primary) !important",
    border: "1px solid var(--app-primary)",
    color: "var(--app-on-primary) !important",
    fontSize: 13,
    fontWeight: 600,
    padding: "9px 14px",
    borderRadius: "9px",
    "&:hover": {
      backgroundColor: "#F59E2E !important",
    },
  },
  newMenuDropdown: {
    width: "300px !important",
    borderRadius: "12px !important",
    // Beats the `.rightBTN .mantine-Menu-dropdown { padding: 0 }` rule above
    // (the dropdown is portalled out of .rightBTN, but keep the doubled class
    // for safety) so items get the same breathing room as the Notes module.
    "&&": {
      padding: "6px !important",
    },
    border: "1px solid var(--app-border) !important",
    boxShadow: "0 12px 32px rgba(16, 24, 40, .16) !important",
  },
  newMenuItemBtn: {
    display: "flex",
    gap: 11,
    alignItems: "center",
    width: "100%",
    textAlign: "left",
    background: "transparent",
    border: "none",
    borderRadius: 9,
    padding: "10px 11px",
    cursor: "pointer",
    fontFamily: "inherit",
    color: "var(--app-text)",
    "&:hover": {
      backgroundColor: "var(--app-bg)",
    },
  },
  newMenuItemBordered: {
    borderTop: "1px solid var(--app-border)",
    borderRadius: "0 0 9px 9px",
  },
  newMenuIco: {
    flex: "none",
    width: 32,
    height: 32,
    borderRadius: 8,
    background: "#FFF3E0",
    color: "#B25E09",
    display: "grid",
    placeItems: "center",
  },
  newMenuInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  newMenuTitle: {
    fontFamily: "Inter",
    fontSize: 13.5,
    fontWeight: 650,
    color: "var(--app-text)",
  },
}));

export default useStyles;
